/**
 * Volatility Analyzer Types
 *
 * Type definitions for the Volatility/Squeeze Regime Analyzer.
 * These types define the API contract between TypeScript and Rust.
 * The Rust implementation mirrors these types with serde annotations.
 *
 * @see docs/VOLATILITY_REGIME_ANALYZER_DESIGN.md for detailed design documentation
 */
// ============================================================================
// Enums
// ============================================================================
/**
 * Volatility Zone Classification
 * 7 zones from extreme compression to extreme expansion
 */
export var VolatilityZone;
(function (VolatilityZone) {
    /** ATR < 0.3x 20-bar avg, BB inside KC */
    VolatilityZone["extremeCompression"] = "extremeCompression";
    /** ATR < 0.5x 20-bar avg, BB width < KC width */
    VolatilityZone["squeeze"] = "squeeze";
    /** ATR < 0.75x 20-bar avg */
    VolatilityZone["lowVolatility"] = "lowVolatility";
    /** ATR within 0.75x - 1.5x of 20-bar avg */
    VolatilityZone["normal"] = "normal";
    /** ATR > 1.5x 20-bar avg */
    VolatilityZone["elevated"] = "elevated";
    /** ATR > 2x 20-bar avg */
    VolatilityZone["highVolatility"] = "highVolatility";
    /** ATR > 3x 20-bar avg (volatility spike/crash) */
    VolatilityZone["extremeExpansion"] = "extremeExpansion";
})(VolatilityZone || (VolatilityZone = {}));
/**
 * Squeeze State Classification
 * TTM Squeeze-style state machine for BB vs KC relationship
 */
export var SqueezeState;
(function (SqueezeState) {
    /** Normal volatility, BB not inside KC */
    SqueezeState["noSqueeze"] = "noSqueeze";
    /** BB fully inside KC for 1-2 bars (not yet confirmed) */
    SqueezeState["squeezing"] = "squeezing";
    /** BB fully inside KC for 3+ bars (confirmed squeeze) */
    SqueezeState["inSqueeze"] = "inSqueeze";
    /** BB expanding, exiting KC (breakout in progress) */
    SqueezeState["breakingOut"] = "breakingOut";
    /** First 5 bars after breakout completed */
    SqueezeState["postBreakout"] = "postBreakout";
})(SqueezeState || (SqueezeState = {}));
/**
 * Price Behavior Classification
 * Combines volatility level and trend strength (ADX)
 */
export var PriceBehavior;
(function (PriceBehavior) {
    /** Low volatility, ADX > 25: Steady grinding, directional */
    PriceBehavior["calmTrend"] = "calmTrend";
    /** Low volatility, ADX < 20: Dead zone, no direction */
    PriceBehavior["calmChop"] = "calmChop";
    /** High volatility, ADX > 25: Strong directional with wide swings */
    PriceBehavior["volatileTrend"] = "volatileTrend";
    /** High volatility, ADX < 20: Whipsaw territory, mean-reversion */
    PriceBehavior["volatileChop"] = "volatileChop";
    /** Middle ground - not clearly calm or volatile */
    PriceBehavior["normal"] = "normal";
})(PriceBehavior || (PriceBehavior = {}));
/**
 * Volatility Trend Classification
 * Direction of volatility change over recent bars
 */
export var VolatilityTrend;
(function (VolatilityTrend) {
    /** ATR increasing over 3 bars */
    VolatilityTrend["expanding"] = "expanding";
    /** ATR decreasing over 3 bars */
    VolatilityTrend["contracting"] = "contracting";
    /** ATR relatively unchanged */
    VolatilityTrend["stable"] = "stable";
})(VolatilityTrend || (VolatilityTrend = {}));
/**
 * Breakout Direction
 */
export var BreakoutDirection;
(function (BreakoutDirection) {
    /** Price broke out above Keltner Channel upper band */
    BreakoutDirection["up"] = "up";
    /** Price broke out below Keltner Channel lower band */
    BreakoutDirection["down"] = "down";
})(BreakoutDirection || (BreakoutDirection = {}));
/**
 * Volatility Event Types
 * All possible events generated by the Volatility analyzer
 * Phase 1 + Phase 2 complete event set
 */
export var VolatilityEventType;
(function (VolatilityEventType) {
    // === Squeeze Events (most actionable) ===
    /** BB went inside KC (confirmed after 3 bars) */
    VolatilityEventType["enteredSqueeze"] = "enteredSqueeze";
    /** Breakout to upside */
    VolatilityEventType["exitedSqueezeUp"] = "exitedSqueezeUp";
    /** Breakout to downside */
    VolatilityEventType["exitedSqueezeDown"] = "exitedSqueezeDown";
    /** Phase 2: Squeeze tightened significantly (BB/KC ratio dropped 10%+) */
    VolatilityEventType["squeezeIntensified"] = "squeezeIntensified";
    /** Phase 2: Momentum direction flipped while in squeeze */
    VolatilityEventType["squeezeMomentumFlipped"] = "squeezeMomentumFlipped";
    // === Zone Transitions (full set - Phase 2) ===
    /** Entered extreme compression zone */
    VolatilityEventType["enteredExtremeCompression"] = "enteredExtremeCompression";
    /** Entered squeeze zone (via ATR, not squeeze state) */
    VolatilityEventType["enteredSqueezeZone"] = "enteredSqueezeZone";
    /** Entered low volatility zone */
    VolatilityEventType["enteredLowVolatility"] = "enteredLowVolatility";
    /** Entered normal zone */
    VolatilityEventType["enteredNormal"] = "enteredNormal";
    /** Entered elevated zone */
    VolatilityEventType["enteredElevated"] = "enteredElevated";
    /** Entered high volatility zone */
    VolatilityEventType["enteredHighVolatility"] = "enteredHighVolatility";
    /** Entered extreme expansion zone */
    VolatilityEventType["enteredExtremeExpansion"] = "enteredExtremeExpansion";
    // === Regime Changes ===
    /** Changed to calm trend regime */
    VolatilityEventType["regimeChangedToCalmTrend"] = "regimeChangedToCalmTrend";
    /** Changed to calm chop regime */
    VolatilityEventType["regimeChangedToCalmChop"] = "regimeChangedToCalmChop";
    /** Changed to volatile trend regime */
    VolatilityEventType["regimeChangedToVolatileTrend"] = "regimeChangedToVolatileTrend";
    /** Changed to volatile chop regime */
    VolatilityEventType["regimeChangedToVolatileChop"] = "regimeChangedToVolatileChop";
    /** Changed to normal regime */
    VolatilityEventType["regimeChangedToNormal"] = "regimeChangedToNormal";
    // === Volatility Extremes ===
    /** ATR jumped > 50% in one bar */
    VolatilityEventType["volatilitySpike"] = "volatilitySpike";
    /** Phase 2: ATR dropped > 30% in one bar */
    VolatilityEventType["volatilityCrash"] = "volatilityCrash";
    /** Phase 2: ATR hit new low in lookback (< 5th percentile) */
    VolatilityEventType["newVolatilityLow"] = "newVolatilityLow";
    /** Phase 2: ATR hit new high in lookback (> 95th percentile) */
    VolatilityEventType["newVolatilityHigh"] = "newVolatilityHigh";
})(VolatilityEventType || (VolatilityEventType = {}));
//# sourceMappingURL=VolatilityAnalyzerTypes.js.map